import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, Grid, IconButton, Stack, Typography } from "@mui/joy";
import {
  AutomaticThought,
  AutomaticThoughtFieldMap,
  CONCEPT_DOMAIN,
  CONCEPT_NOTE_AUTOMATIC_THOUGHT_LIST_ENDPOINT,
  conceptApi,
} from "api/conceptApi";

import DeleteAutomaticThoughtButton from "components/coachRightSidebar/conceptNote/DeleteAutomaticThoughtButton";

import AutomaticThoughtInput from "./AutomaticThoughtInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import AutomaticThoughtConfirmButton from "./AutomaticThoughtConfirmButton";
import ResetAutomaticThoughtButton from "../ResetAutomaticThoughtButton";
import ReferenceLink from "../ReferenceLink";
import { UpArrowSvg } from "static/image/common/SvgAssets";
import { theme } from "styles/theme";

interface AutomaticThoughtForValueListItemProps {
  automaticThought: AutomaticThought;
  onDelete?: (automaticThoughtId: string) => void;
  highlight?: boolean;
  totalCount?: number; // Total count for smart collapse
}

const AutomaticThoughtForValueListItem = React.memo(
  function AutomaticThoughtForValueListItem({
    automaticThought,
    onDelete,
    highlight = false,
    totalCount = 0,
  }: AutomaticThoughtForValueListItemProps) {
    const [localData, setLocalData] =
      useState<AutomaticThought>(automaticThought);
    const queryClient = useQueryClient();
    const patientState = useRecoilValue(patientAtom);
    const [inputDisable, setInputDisable] = useState<boolean>(false);

    // Only collapse completed items for clean UX
    const [isCollapsed, setIsCollapsed] = useState<boolean>(
      localData.taskProgressStatus === "completed"
    );

    useEffect(() => {
      setLocalData(automaticThought);
    }, [automaticThought]);

    // Optimize: Convert state to computed value with useMemo
    const canBeConfirmed = useMemo(() => {
      const validSituation = !!localData.situation?.trim();
      const validThoughtPositive = !!localData.thoughtPositive?.trim();
      const validValueDomain = !!localData.valueDomain?.trim();
      const validValue = !!localData.value?.trim();
      const validEmotion = !!localData.emotion?.trim();
      const validPhysicalResponse = !!localData.physicalResponse?.trim();
      const validBehavior = !!localData.behavior?.trim();

      return (
        validSituation &&
        validThoughtPositive &&
        validValueDomain &&
        validValue &&
        validEmotion &&
        validPhysicalResponse &&
        validBehavior
      );
    }, [localData]);

    useEffect(() => {
      if (localData.taskProgressStatus === "completed") {
        setInputDisable(true);
      }
    }, [localData.taskProgressStatus]);

    const { mutate: updateAutomaticThought } = useMutation<
      void,
      unknown,
      AutomaticThoughtFieldMap
    >(
      async (payload) => {
        await conceptApi.updateAutomaticThought(
          patientState?.patientId!!,
          automaticThought.automaticThoughtId,
          payload,
          true
        );
      },
      {
        onSuccess: () => {
          // 저장 결과를 목록 캐시에 반영 — 탭 전환 등으로 항목이 리마운트될 때
          // 저장 전 데이터로 되돌아가지 않도록 함
          queryClient.invalidateQueries([
            CONCEPT_DOMAIN,
            CONCEPT_NOTE_AUTOMATIC_THOUGHT_LIST_ENDPOINT,
            patientState?.patientId,
          ]);
        },
        onError: (error) => {
          console.error("Note update failed:", error);
        },
      }
    );

    const handleSaveSituation = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        situation: newValue,
      }));

      updateAutomaticThought({
        situation: newValue,
      });
    };

    const handleSaveThoughtPositive = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        thoughtPositive: newValue,
      }));

      updateAutomaticThought({
        thoughtPositive: newValue,
      });
    };

    const handleSaveValueDomain = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        valueDomain: newValue,
      }));

      updateAutomaticThought({
        valueDomain: newValue,
      });
    };

    const handleSaveValue = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        value: newValue,
      }));

      updateAutomaticThought({
        value: newValue,
      });
    };

    const handleSaveEmotion = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        emotion: newValue,
      }));

      updateAutomaticThought({
        emotion: newValue,
      });
    };

    const handleSavePhysicalResponse = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        physicalResponse: newValue,
      }));

      updateAutomaticThought({
        physicalResponse: newValue,
      });
    };

    const handleSaveBehavior = (newValue: string) => {
      setLocalData((prev) => ({
        ...prev,
        behavior: newValue,
      }));

      updateAutomaticThought({
        behavior: newValue,
      });
    };

    return (
      <Stack
        spacing={1}
        p={0.3}
        sx={{
          ...(highlight && {
            borderRadius: "4px",
            boxShadow: `0 0 0 1px rgba(15,85,167,0.3)`,
          }),
          backgroundColor: "unset",
          transition: "background-color 0.3s ease",
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{ minWidth: "24px", minHeight: "24px" }}
            >
              <Box
                sx={{
                  ...(isCollapsed && {
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                  }),
                  transition: "transform 0.2s",
                }}
              >
                <UpArrowSvg color={theme.vars.palette.Content.Weak} />
              </Box>
            </IconButton>
            <Typography
              variant={"Footnote"}
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{
                fontWeight: 500,
                cursor: "pointer",
                minWidth: "35px",
                color: theme.vars.palette.Content.Strong,
              }}
            >
              가치 {localData.sequence}
            </Typography>

            <ReferenceLink
              reference={"기록 보기"}
              thoughtRecordKey={localData.thoughtRecordKey}
            />
            {(localData.automaticThoughtStatus === "note_confirmed" ||
              localData.automaticThoughtStatus === "confirmed") && (
              <>
                {localData.taskProgressStatus === "in_progress" ? (
                  <Chip size="sm" color="success" variant="outlined">
                    진행 중
                  </Chip>
                ) : localData.taskProgressStatus === "completed" ? (
                  <Chip size="sm" color="neutral" variant="outlined">
                    완료
                  </Chip>
                ) : (
                  <Chip size="sm" color="warning" variant="outlined">
                    대기 중
                  </Chip>
                )}
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {localData.automaticThoughtStatus !== "preparing" &&
              localData.taskProgressStatus !== "completed" && (
                <ResetAutomaticThoughtButton
                  automaticThoughtId={localData.automaticThoughtId}
                  thoughtRecordKey={localData.thoughtRecordKey}
                  isForValue={true}
                />
              )}
            {localData.automaticThoughtStatus === "preparing" && onDelete && (
              <DeleteAutomaticThoughtButton
                automaticThoughtId={localData.automaticThoughtId}
                thoughtRecordKey={localData.thoughtRecordKey}
                isForValue={true}
              />
            )}
            {localData.automaticThoughtStatus === "preparing" && (
              <AutomaticThoughtConfirmButton
                automaticThoughtId={localData.automaticThoughtId}
                disabled={!canBeConfirmed}
                isForValue={true}
              />
            )}
          </Stack>
        </Stack>

        {/* Collapsible content - only render when expanded */}
        {!isCollapsed && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {/* 상황 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  상황
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="textarea"
                  initialValue={localData.situation ?? ""}
                  onSave={handleSaveSituation}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>

            {/* 생각 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  생각
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="textarea"
                  isForValue={true}
                  initialValue={localData.thoughtPositive ?? ""}
                  onSave={handleSaveThoughtPositive}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>

            {/* 가치 영역 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  가치 영역
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="select"
                  isForValue={true}
                  initialValue={localData.valueDomain ?? ""}
                  onSave={handleSaveValueDomain}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>

            {/* 가치 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  가치
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="textarea"
                  initialValue={localData.value ?? ""}
                  onSave={handleSaveValue}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>

            {/* 감정 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  감정
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="textarea"
                  initialValue={localData.emotion ?? ""}
                  onSave={handleSaveEmotion}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>

            {/* 신체 반응 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  신체 반응
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="textarea"
                  initialValue={localData.physicalResponse ?? ""}
                  onSave={handleSavePhysicalResponse}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>

            {/* 행동 */}
            <Grid container>
              <Grid xs={2.5} sx={{ pt: "10px" }}>
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  행동
                </Typography>
              </Grid>
              <Grid xs={9.5}>
                <AutomaticThoughtInput
                  type="textarea"
                  initialValue={localData.behavior ?? ""}
                  onSave={handleSaveBehavior}
                  disable={inputDisable}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </Stack>
    );
  }
);

export default AutomaticThoughtForValueListItem;
